/**
 * Harness `/execute` 的流式传输层。
 * SSE 的 `requires_action` 表示当前 Run 暂停，不等同于 `done` 或前端执行 Tool。
 */
import { asNonEmptyString, harnessFetch } from './client'
import { parseHarnessRunView } from './mappers'
import type {
  AssistantStreamEvent,
  AssistantToolResultTurn,
  AssistantUserTurn,
  HarnessSseFrame,
} from './types'

export function splitHarnessSseFrames(buffer: string): {
  frames: HarnessSseFrame[]
  remainder: string
} {
  const rawFrames = buffer.split(/\r?\n\r?\n/)
  const frames = rawFrames
    .slice(0, -1)
    .reduce<HarnessSseFrame[]>((result, frame) => {
      let event = 'message'
      const data: string[] = []
      frame.split(/\r?\n/).forEach((line) => {
        if (line.startsWith('event:')) {
          event = line.slice(6).trim() || 'message'
        }
        if (line.startsWith('data:')) {
          data.push(line.slice(5).trim())
        }
      })
      if (data.length) {
        result.push({ event, data: data.join('\n') })
      }
      return result
    }, [])

  return { frames, remainder: rawFrames[rawFrames.length - 1] }
}

export async function executeAssistantRun(
  turn: AssistantUserTurn | AssistantToolResultTurn,
  signal: AbortSignal,
  onEvent: (event: AssistantStreamEvent) => void,
  fetchImpl?: typeof fetch
): Promise<void> {
  const body: {
    messages: Array<
      | { role: 'user'; content: string }
      | { role: 'tool'; tool_call_id: string; content: Record<string, unknown> }
    >
    stream: true
    conversation_id?: string
    run_id?: string
    santi_type?: 'gg_ai_assistant'
    tools?: Array<any>
    system_prompt?: string
  } = { messages: [], stream: true }
  if ('toolCallId' in turn) {
    body.messages = [
      { role: 'tool', tool_call_id: turn.toolCallId, content: turn.content },
    ]
    body.conversation_id = turn.conversationId
    body.run_id = turn.runId
  } else {
    const content = turn.content.trim()
    if (!content) throw new Error('消息内容不能为空')
    body.messages = [{ role: 'user', content }]
    body.tools = turn.tools
    if (turn.systemPrompt) body.system_prompt = turn.systemPrompt
    if (turn.conversationId) body.conversation_id = turn.conversationId
    else body.santi_type = 'gg_ai_assistant'
  }

  const response = await harnessFetch('/execute', {
    method: 'POST',
    body,
    signal,
    fetchImpl,
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
  })
  if (!response.body) {
    throw new Error('Agent 服务未返回流数据')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let terminal = false
  try {
    while (!terminal) {
      const result = await reader.read()
      if (result.done) {
        break
      }

      buffer += decoder.decode(result.value, { stream: true })
      const split = splitHarnessSseFrames(buffer)
      buffer = split.remainder
      for (const frame of split.frames) {
        const event = parseHarnessSseEvent(frame)
        onEvent(event)

        // 这些事件均结束当前 HTTP 流；queued/accepted 后续只能按 runId 查询。
        if (
          event.type === 'queued' ||
          event.type === 'accepted' ||
          event.type === 'requires_action' ||
          event.type === 'done' ||
          event.type === 'error'
        ) {
          terminal = true
          await reader.cancel()
          break
        }
      }
    }
  } finally {
    reader.releaseLock()
  }

  if (!terminal) {
    throw new Error('Agent 服务在完成前关闭了连接')
  }
}

function parseHarnessSseEvent(frame: HarnessSseFrame): AssistantStreamEvent {
  let envelope: {
    type?: unknown
    conversation_id?: unknown
    run_id?: unknown
    errorCode?: unknown
    message?: unknown
    detail?: unknown
    data?: unknown
  }
  try {
    envelope = JSON.parse(frame.data) as typeof envelope
  } catch {
    throw new Error('Agent 服务返回了无效流数据')
  }
  const type = typeof envelope.type === 'string' ? envelope.type : ''
  const data = envelope.data as {
    queue_sequence?: unknown
    queue_position?: unknown
  } | null
  const conversationId = asNonEmptyString(envelope.conversation_id)
  const runId = asNonEmptyString(envelope.run_id)
  if ((type === 'queued' || type === 'accepted') && runId) {
    return {
      type,
      conversationId: conversationId || undefined,
      runId,
      queueSequence:
        typeof data?.queue_sequence === 'number'
          ? data.queue_sequence
          : undefined,
      queuePosition:
        typeof data?.queue_position === 'number'
          ? data.queue_position
          : undefined,
    }
  }
  if (type === 'start' && conversationId && runId) {
    return { type, conversationId, runId }
  }
  if (type === 'message_delta' && conversationId && runId) {
    const data = envelope.data as { content?: unknown } | null
    if (typeof data?.content !== 'string') {
      throw new Error('Agent 服务返回了无效消息增量')
    }
    return { type, conversationId, runId, content: data.content }
  }
  if (type === 'requires_action' || type === 'done') {
    return {
      type,
      run: parseHarnessRunView(envelope.data, conversationId, runId),
    }
  }
  if (type === 'error') {
    const data = envelope.data as { code?: unknown; message?: unknown } | null
    return {
      type,
      conversationId: conversationId || undefined,
      runId: runId || undefined,
      code:
        asNonEmptyString(data?.code) ||
        asNonEmptyString(envelope.errorCode) ||
        undefined,
      message:
        asNonEmptyString(data?.message) ||
        asNonEmptyString(envelope.detail) ||
        asNonEmptyString(envelope.message) ||
        'Agent 服务返回错误',
    }
  }
  return { type: 'diagnostic', event: type || frame.event, data: envelope.data }
}
